import { useMemo } from "react"
import {
  LIVE_DATA_REFETCH_MS,
  useConvexQueries,
  useConvexQuery,
} from "@/hooks/use-convex-query"
import { Link } from "react-router-dom"
import { api } from "../../convex/_generated/api"
import { cn } from "@/lib/utils"
import { ArrowRight } from "lucide-react"
import type { Doc } from "../../convex/_generated/dataModel"

type CategoryDoc = Doc<"categories">
type BrandDoc = Doc<"brands">
type ProductDoc = Doc<"products">

const CATEGORY_STYLES: Record<
  string,
  {
    borderColor: string
    bgColor: string
    headerBg: string
    headerTextDark: boolean
    dotColor: string
  }
> = {
  pink: {
    borderColor: "border-zielarz-purple/40",
    bgColor: "bg-zielarz-purple/10",
    headerBg: "border-b border-zielarz-purple/30 bg-zielarz-purple/25",
    headerTextDark: false,
    dotColor: "bg-zielarz-purple/90",
  },
  amber: {
    borderColor: "border-zielarz-yellow/45",
    bgColor: "bg-zielarz-yellow/10",
    headerBg: "border-b border-zielarz-yellow/35 bg-zielarz-yellow/25",
    headerTextDark: true,
    dotColor: "bg-zielarz-yellow/90",
  },
  green: {
    borderColor: "border-zielarz-green/40",
    bgColor: "bg-zielarz-green/10",
    headerBg: "border-b border-zielarz-green/30 bg-zielarz-green/25",
    headerTextDark: false,
    dotColor: "bg-zielarz-green/90",
  },
}

export function FlavorsPreview() {
  const categories = useConvexQuery(
    api.categories.list,
    {},
    {
      refetchInterval: LIVE_DATA_REFETCH_MS,
    }
  ) as CategoryDoc[] | undefined

  if (!categories) {
    return (
      <section id="smaki" className="relative py-24">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-2 border-zielarz-green border-t-transparent" />
        </div>
      </section>
    )
  }

  return (
    <section id="smaki" className="relative py-24">
      <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-zielarz-green/30 to-transparent" />

      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Sprawdz dostepne <span className="text-zielarz-green">smaki</span>
          </h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Przegladaj nasz aktualny asortyment. Wyszukaj ulubiony smak lub
            przegladaj wedlug kategorii i marki.
          </p>
        </div>

        {/* Category cards - preview */}
        <div className="mb-10 grid gap-6 md:grid-cols-3">
          {categories.map((category) => (
            <CategoryPreviewCard key={category._id} category={category} />
          ))}
        </div>

        {/* CTA to full page */}
        <div className="text-center">
          <Link
            to="/smaki"
            className="inline-flex items-center gap-2 rounded-full bg-zielarz-green px-8 py-3 text-base font-bold text-white transition-all hover:bg-zielarz-green-light hover:shadow-lg hover:shadow-zielarz-green/25"
          >
            Zobacz pelna liste smakow
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}

function CategoryPreviewCard({ category }: { category: CategoryDoc }) {
  const brands = useConvexQuery(
    api.brands.listByCategory,
    { categoryId: category._id },
    { refetchInterval: LIVE_DATA_REFETCH_MS }
  ) as BrandDoc[] | undefined

  const queriesMap = useMemo(
    () =>
      Object.fromEntries(
        (brands ?? []).map((brand) => [
          brand._id,
          {
            query: api.products.listByBrand,
            args: { brandId: brand._id },
          },
        ])
      ),
    [brands]
  )

  const productsByBrand = useConvexQueries(queriesMap) as
    | Record<string, ProductDoc[] | undefined | Error>
    | undefined

  const style =
    CATEGORY_STYLES[category.color || "green"] ?? CATEGORY_STYLES.green

  if (!brands || !productsByBrand) {
    return (
      <div
        className={cn(
          "rounded-2xl border p-8 text-center",
          style.borderColor,
          style.bgColor
        )}
      >
        <div className="inline-block size-6 animate-spin rounded-full border-2 border-zielarz-green border-t-transparent" />
      </div>
    )
  }

  const brandsWithAvailableProducts = brands
    .map((brand) => {
      const products = productsByBrand[brand._id]
      if (!products || products instanceof Error) return null

      const availableProducts = products.filter(
        (product) => product.isAvailable
      )
      if (availableProducts.length === 0) return null

      return { brand, availableProducts }
    })
    .filter(
      (value): value is { brand: BrandDoc; availableProducts: ProductDoc[] } =>
        value !== null
    )

  const previewBrands = brandsWithAvailableProducts.slice(0, 2)
  const remainingBrandsCount =
    brandsWithAvailableProducts.length - previewBrands.length

  return (
    <Link
      to={"/smaki?kategoria=" + category._id}
      className={cn(
        "group block overflow-hidden rounded-2xl border transition-shadow hover:shadow-lg hover:shadow-zielarz-green/5",
        style.borderColor,
        style.bgColor
      )}
    >
      <div className={cn("px-6 py-4 text-center", style.headerBg)}>
        <h3
          className={cn(
            "text-2xl font-extrabold tracking-wider drop-shadow-[0_1px_0_rgba(0,0,0,0.08)]",
            style.headerTextDark ? "text-black" : "text-white"
          )}
        >
          {category.name}
        </h3>
      </div>

      <div className="divide-y divide-border/30">
        {previewBrands.map(({ brand, availableProducts }) => (
          <BrandMiniPreview
            key={brand._id}
            brand={brand}
            availableProducts={availableProducts}
            categoryStyle={style}
          />
        ))}
      </div>

      {remainingBrandsCount > 0 && (
        <div className="border-t border-border/30 px-5 py-3 text-center text-xs text-muted-foreground">
          ...i {remainingBrandsCount} wiecej marek
        </div>
      )}

      <div className="border-t border-border/30 px-5 py-3 text-center">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-zielarz-green/85 transition-all group-hover:gap-2 group-hover:text-zielarz-green">
          Przegladaj {category.name.toLowerCase()}
          <ArrowRight className="size-3" />
        </span>
      </div>
    </Link>
  )
}

function BrandMiniPreview({
  brand,
  availableProducts,
  categoryStyle,
}: {
  brand: BrandDoc
  availableProducts: ProductDoc[]
  categoryStyle: (typeof CATEGORY_STYLES)[string]
}) {
  const preview = availableProducts.slice(0, 4)

  return (
    <div className="px-5 py-3">
      <div className="mb-1.5 flex items-center justify-between">
        <h4 className="text-xs font-bold tracking-wide uppercase">
          {brand.name}
        </h4>
        <span className="text-[10px] text-muted-foreground">
          {brand.price10ml}zl/10ml
        </span>
      </div>
      <ul className="space-y-0.5">
        {preview.map((product) => (
          <li
            key={product._id}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span
              className={cn(
                "size-1 shrink-0 rounded-full",
                categoryStyle.dotColor
              )}
            />
            {product.name}
          </li>
        ))}
        {availableProducts.length > 4 && (
          <li className="text-[10px] text-muted-foreground/70">
            +{availableProducts.length - 4} wiecej
          </li>
        )}
      </ul>
    </div>
  )
}
